import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getFixtures, createFixture, updateFixture, deleteFixture } from './coachApi';

const FIXTURE_TYPES = [
  { id: 'match',      icon: '⚽', nl: 'Wedstrijd',    en: 'Match',       color: '#5B9ED0' },
  { id: 'training',   icon: '🎓', nl: 'Training',     en: 'Training',    color: '#7B6FD0' },
  { id: 'friendly',   icon: '🤝', nl: 'Oefenwedstrijd', en: 'Friendly',  color: '#2A9E82' },
  { id: 'tournament', icon: '🏆', nl: 'Toernooi',     en: 'Tournament',  color: '#C8A84B' },
  { id: 'camp',       icon: '⛺', nl: 'Trainingskamp', en: 'Training Camp', color: '#D4873A' },
  { id: 'other',      icon: '📌', nl: 'Overig',       en: 'Other',       color: C.silver },
];

const RESULT_COLORS = { win: '#4AC87A', draw: '#C8A84B', loss: '#C05050' };

const EMPTY_FORM = { type: 'match', date: '', time: '', opponent: '', venue: '', home: true, competition: '', notes: '', result: '', score_home: '', score_away: '' };

export default function CoachScheduleTab({ coachId, teams, lang }) {
  const nl = lang === 'nl';
  const [fixtures, setFixtures]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filter, setFilter]       = useState('upcoming');
  const [teamFilter, setTeamFilter] = useState('all');

  const load = useCallback(() => {
    getFixtures(coachId).then(d => { if (Array.isArray(d)) setFixtures(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();

  const filtered = fixtures.filter(f => {
    const d = new Date(`${f.date}T${f.time || '00:00'}`);
    if (filter === 'upcoming') { if (d < now) return false; }
    if (filter === 'past')     { if (d >= now) return false; }
    if (teamFilter !== 'all' && f.team_id !== teamFilter) return false;
    return true;
  }).sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

  const handleSave = async () => {
    if (!form.date) return;
    setSaving(true);
    if (editId) {
      await updateFixture(coachId, editId, form).catch(() => {});
    } else {
      await createFixture(coachId, { ...form, coach_id: coachId }).catch(() => {});
    }
    setSaving(false);
    setShowNew(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Fixture verwijderen?' : 'Delete fixture?')) return;
    await deleteFixture(coachId, id).catch(() => {});
    load();
  };

  const openEdit = (f) => {
    setForm({ type: f.type, date: f.date, time: f.time || '', opponent: f.opponent || '', venue: f.venue || '', home: f.home !== false, competition: f.competition || '', notes: f.notes || '', result: f.result || '', score_home: f.score_home || '', score_away: f.score_away || '' });
    setEditId(f.id);
    setShowNew(true);
  };

  const setResult = async (f, result) => {
    await updateFixture(coachId, f.id, { ...f, result }).catch(() => {});
    load();
  };

  const typeOf = id => FIXTURE_TYPES.find(t => t.id === id) || FIXTURE_TYPES[FIXTURE_TYPES.length - 1];

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Wedstrijdschema' : 'Match Schedule'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{fixtures.length} {nl ? 'fixtures' : 'fixtures'}</div>
        </div>
        <button onClick={() => { setShowNew(v => !v); setEditId(null); setForm(EMPTY_FORM); }}
          style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
          + {nl ? 'Toevoegen' : 'Add'}
        </button>
      </div>

      {/* New/Edit form */}
      {showNew && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            {editId ? (nl ? 'Fixture bewerken' : 'Edit fixture') : (nl ? 'Nieuwe fixture' : 'New fixture')}
          </div>

          {/* Type */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {FIXTURE_TYPES.map(t => (
              <button key={t.id} onClick={() => setForm(f => ({ ...f, type: t.id }))}
                style={{ padding: '5px 10px', borderRadius: 20, border: `1.5px solid ${form.type === t.id ? t.color : _T.cardBorderBot}`, background: form.type === t.id ? `${t.color}20` : _T.bg4, color: form.type === t.id ? t.color : C.silver, fontSize: 11, cursor: 'pointer' }}>
                {t.icon} {nl ? t.nl : t.en}
              </button>
            ))}
          </div>

          {/* Date + time */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
          </div>

          {/* Opponent + venue */}
          <input value={form.opponent} onChange={e => setForm(f => ({ ...f, opponent: e.target.value }))}
            placeholder={nl ? 'Tegenstander' : 'Opponent'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <input value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
            placeholder={nl ? '📍 Locatie / stadion' : '📍 Venue / stadium'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <input value={form.competition} onChange={e => setForm(f => ({ ...f, competition: e.target.value }))}
            placeholder={nl ? 'Competitie / toernooi' : 'Competition / tournament'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />

          {/* Home / away toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <button onClick={() => setForm(f => ({ ...f, home: !f.home }))}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.home ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${form.home ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', flexShrink: 0, padding: 0 }}>
              <div style={{ position: 'absolute', top: 2, left: form.home ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: form.home ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
            </button>
            <span style={{ color: C.silver, fontSize: 12 }}>🏠 {nl ? (form.home ? 'Thuiswedstrijd' : 'Uitwedstrijd') : (form.home ? 'Home' : 'Away')}</span>
          </div>

          {/* Team selector */}
          {teams?.length > 0 && (
            <select value={form.team_id || ''} onChange={e => setForm(f => ({ ...f, team_id: e.target.value }))}
              style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }}>
              <option value="">{nl ? 'Team (optioneel)' : 'Team (optional)'}</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.sport} {t.name}</option>)}
            </select>
          )}

          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder={nl ? 'Aantekeningen (optioneel)' : 'Notes (optional)'}
            rows={2}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowNew(false); setEditId(null); }} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleSave} disabled={saving || !form.date}
              style={{ flex: 1, background: form.date ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.date ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '10px', cursor: form.date ? 'pointer' : 'not-allowed' }}>
              {saving ? '...' : (nl ? 'Opslaan' : 'Save')}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {[['upcoming', nl ? 'Aankomend' : 'Upcoming'], ['past', nl ? 'Gespeeld' : 'Played'], ['all', nl ? 'Alles' : 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${filter === v ? _T.accent : _T.cardBorderBot}`, background: filter === v ? `${_T.accent}20` : _T.bg4, color: filter === v ? _T.accent : C.silver, fontSize: 11, fontWeight: filter === v ? 700 : 400, cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div> : (
        filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📅</div>
            <div style={{ fontSize: 14 }}>{nl ? 'Geen fixtures gevonden' : 'No fixtures found'}</div>
          </div>
        ) : (
          filtered.map(f => {
            const tc = typeOf(f.type);
            const d  = new Date(`${f.date}T${f.time || '00:00'}`);
            const isPast = d < now;
            const resultColor = RESULT_COLORS[f.result];
            return (
              <div key={f.id} style={{ background: _T.bg3, border: `1.5px solid ${resultColor ? resultColor + '44' : _T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${tc.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{tc.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>
                          {f.opponent || (nl ? tc.nl : tc.en)}
                          {f.home !== undefined && <span style={{ fontSize: 11, color: C.silver2, marginLeft: 6 }}>{f.home ? '🏠' : '✈️'}</span>}
                        </div>
                        {f.competition && <div style={{ color: C.silver2, fontSize: 11 }}>{f.competition}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ color: resultColor || (isPast ? C.silver : _T.accent), fontSize: 11, fontWeight: 700 }}>
                          {f.result ? f.result.toUpperCase() : d.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' })}
                        </div>
                        <div style={{ color: C.silver2, fontSize: 10 }}>{f.time || ''}</div>
                      </div>
                    </div>
                    {f.venue && <div style={{ color: C.silver2, fontSize: 11, marginTop: 3 }}>📍 {f.venue}</div>}

                    {/* Result entry for past fixtures */}
                    {isPast && !f.result && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <span style={{ color: C.silver2, fontSize: 11, alignSelf: 'center' }}>{nl ? 'Uitslag:' : 'Result:'}</span>
                        {[['win', nl ? 'Gewonnen' : 'Won', '#4AC87A'], ['draw', nl ? 'Gelijkspel' : 'Draw', '#C8A84B'], ['loss', nl ? 'Verloren' : 'Lost', '#C05050']].map(([r, l, col]) => (
                          <button key={r} onClick={() => setResult(f, r)}
                            style={{ padding: '3px 8px', borderRadius: 6, border: `1px solid ${col}55`, background: `${col}18`, color: col, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button onClick={() => openEdit(f)} style={{ background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 7, color: C.silver, fontSize: 10, padding: '3px 7px', cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => handleDelete(f.id)} style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer', padding: 0 }}>×</button>
                  </div>
                </div>
              </div>
            );
          })
        )
      )}
    </div>
  );
}
